import React, { useState } from "react";
import classNames from "classnames/bind";
import useSWR from "swr";
import { Form, Field } from "react-final-form";

import { CATEGORIES } from "../../api/categories";
import { Button } from "../../components/Button";

import { DietanamnezItemForm } from "types/DietanamnezItem";

import styles from "./DietanamnezTileForm.module.css";
import { FrequencyList } from "components/FrequencyList";

const cx = classNames.bind(styles);

type Props = {
  initialValues?: DietanamnezItemForm;
  onSubmit: (values?: DietanamnezItemForm) => Promise<void>;
  unitLabel: string;
  portions: number[];
  tutorialWasShown: boolean;
};

export const DietanamnezTileForm: React.FC<Props> = ({
  initialValues,
  onSubmit,
  unitLabel,
  portions,
  tutorialWasShown,
}) => {
  const [state, setState] = useState<"FILLED" | "DEFAULT" | "FIELD">(
    initialValues ? "FILLED" : "DEFAULT"
  );
  const { data: frequencies = [] } = useSWR<
    {
      key: string;
      custom_частота: string;
    }[]
  >([CATEGORIES.FREQUENCY, 86]);

  if (!frequencies.length || !tutorialWasShown) {
    return null;
  }

  return (
    <Form<Partial<DietanamnezItemForm>>
      initialValues={{
        custom_количество: initialValues?.custom_количество,
        custom_частота_употребления: initialValues?.custom_частота_употребления,
      }}
      validate={(values: Partial<DietanamnezItemForm>) => {
        let errors: { [key: string]: string } = {};

        if (!values.custom_количество || values.custom_количество <= 0) {
          errors.custom_количество = " ";
        }

        if (!values.custom_частота_употребления) {
          errors.custom_частота_употребления = " ";
        }

        if (Object.keys(errors).length) {
          return errors;
        }

        return undefined;
      }}
      validateOnMount
      onSubmit={(values) => onSubmit(values as DietanamnezItemForm)}
    >
      {({ handleSubmit, values, form, valid, submitting }) => (
        <form onSubmit={handleSubmit}>
          {state === "DEFAULT" && (
            <>
              <p className={cx("title")}>Как часто вы едите это блюдо?</p>
              <FrequencyList
                items={frequencies}
                onSubmit={onSubmit}
                onChange={(key) => {
                  form.change("custom_частота_употребления", key);
                  setState("FIELD");
                }}
              />
            </>
          )}
          {state === "FILLED" && (
            <>
              <p className={cx("title", "title_frequency")}>Вы выбрали</p>
              <p className={cx("title")}>
                {
                  frequencies.find(
                    (item) => item.key === values.custom_частота_употребления
                  )!.custom_частота
                }{" "}
                по {values.custom_количество} {unitLabel}
              </p>
              <div className={cx("actions")}>
                <div className={cx("button")}>
                  <Button
                    block
                    type="button"
                    size="medium"
                    onClick={() => setState("DEFAULT")}
                  >
                    Изменить
                  </Button>
                </div>
              </div>
            </>
          )}
          {state === "FIELD" && (
            <>
              <p className={cx("title", "title_frequency")}>
                {
                  frequencies.find(
                    (item) => item.key === values.custom_частота_употребления
                  )!.custom_частота
                }
              </p>
              <div className={cx("inputContainer")}>
                <Field name="custom_количество" type="number">
                  {({ input, meta }) => (
                    <>
                      <input
                        type="number"
                        className={cx("input")}
                        placeholder="Объем порции в граммах"
                        {...input}
                      />
                      <div
                        className={cx("portionDropdown", {
                          portionDropdown_active: meta.active,
                        })}
                      >
                        {portions.map((portion) => (
                          <div
                            key={portion}
                            className={cx("portionDropdownItem")}
                            onClick={() => input.onChange(portion)}
                          >
                            {portion}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </Field>
              </div>
              <footer className={cx("footer")}>
                <div className={cx("button")}>
                  <Button
                    block
                    color="white"
                    type="button"
                    size="medium"
                    onClick={() => {
                      form.change("custom_частота_употребления", undefined);
                      setState("DEFAULT");
                      window.scrollTo(0, 0);
                    }}
                    disabled={submitting}
                  >
                    Отмена
                  </Button>
                </div>
                <div className={cx("button")}>
                  <Button
                    block
                    type="submit"
                    size="medium"
                    disabled={!valid}
                    isLoading={submitting}
                    onClick={() => window.scrollTo(0, 0)}
                  >
                    Продолжить
                  </Button>
                </div>
              </footer>
            </>
          )}
        </form>
      )}
    </Form>
  );
};
